// The Button primitive — the single home for button treatment.
//
// Before this file there were 172 raw <button> elements in poiesis/src and no
// Button component; the styling existed only as two constants in ModalFrame
// (BTN_PRIMARY / BTN_QUIET) plus a third copy in AuthCard (PRIMARY_BTN), and
// as literal class strings pasted into call sites. Nothing here is NEW design:
// every variant below is derived from class strings that already shipped, and
// every (variant, size) pair is evidenced by real call sites — a variant or a
// size nobody uses is a liability, so unevidenced ones were dropped rather
// than invented (notably: no `destructive` variant — the four destructive
// buttons in the codebase have four DIFFERENT class strings, so there is
// nothing to standardise on yet; see the SEN primitives report).
//
// Guarded by __guards__/shared-primitives.test.ts (rule `button-variant`):
// re-pasting one of these class strings onto a raw <button> fails CI.
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { registerPrimitive } from './primitives/registry'

/** Join class fragments, dropping empties and collapsing whitespace, so a
 *  variant template with an empty size slot doesn't emit a double space. */
function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ').trim().replace(/\s+/g, ' ')
}

/**
 * SIZES — padding + type-size pairs OBSERVED in the codebase, not a scale
 * invented here. Each is annotated with what it came from.
 */
export const BUTTON_SIZES = {
  /** BTN_PRIMARY's own pair. */
  md: 'px-3 py-1.5 text-sm',
  /** BTN_QUIET's own pair; also the small primary in CollectionTable. */
  sm: 'px-2.5 py-1.5 text-xs',
  /** The outline/toolbar pair (WidgetEditor, SettingsForm, FilteredEmptyState). */
  xs: 'px-2.5 py-1 text-xs',
  /** Popover menu rows (DashboardPage widget menu, reference + select editors). */
  menu: 'px-2 py-1.5 text-sm',
  /** Square icon button (Sidebar collapse, SidebarIdentity). */
  icon: 'p-1',
  /** Tighter square icon button (Sidebar section toggles). */
  iconSm: 'p-0.5',
  /** Unboxed — link/bare carry no padding of their own. */
  none: '',
} as const
export type ButtonSize = keyof typeof BUTTON_SIZES

/**
 * VARIANTS — each is a template with one slot for the size classes, chosen so
 * that filling the slot reproduces the class string that already shipped.
 * `primary(md)` IS BTN_PRIMARY; `quiet(sm)` IS BTN_QUIET, character for
 * character. (Class ATTRIBUTE order is irrelevant to CSS — cascade uses the
 * generated stylesheet's order — so where a template reorders two tokens the
 * rendered result is identical; Button.equivalence.test.ts proves set equality
 * against every converted call site's original string.)
 */
const VARIANT_TEMPLATE = {
  primary: (size: string) =>
    cx('rounded-md bg-primary', size, 'font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50'),
  quiet: (size: string) =>
    cx('rounded-md', size, 'font-medium text-muted-foreground hover:bg-muted disabled:opacity-50'),
  outline: (size: string) =>
    cx('rounded-md border border-border', size, 'text-foreground hover:bg-muted'),
  ghost: (size: string) =>
    cx('rounded-sm', size, 'text-muted-foreground hover:bg-muted hover:text-foreground'),
  menuItem: (size: string) =>
    cx('rounded-sm', size, 'text-left hover:bg-muted'),
  link: (size: string) =>
    cx(size, 'underline hover:text-foreground'),
  bare: (size: string) =>
    cx(size, 'text-muted-foreground hover:text-foreground'),
} as const
export type ButtonVariant = keyof typeof VARIANT_TEMPLATE

/**
 * EVIDENCE — the one table. It drives the default size, the showcase
 * descriptor, AND the CI guard, so those three can never disagree.
 *
 * `sizes`  : the sizes this variant is actually used at. A size nobody uses is
 *            a liability, so unevidenced pairs are absent, not "available".
 * `boxed`  : does the variant own a box (radius + surface + padding)? Boxed
 *            signatures are distinctive enough that a raw <button> merely
 *            CONTAINING one is a re-roll; the unboxed two (`link`, `bare`) are
 *            two generic text utilities that legitimately co-occur, so only an
 *            exact copy of them is evidence. The guard uses this distinction.
 */
const EVIDENCE: Record<ButtonVariant, {
  purpose: string
  sizes: readonly ButtonSize[]
  boxed: boolean
  /**
   * The classes that CONCLUSIVELY name this variant, independent of size.
   * Matching the full signature is not enough for a guard: a paste that is the
   * canonical set minus exactly one class (`outline` minus text-foreground —
   * dashboards/DashboardPage.tsx) is exactly the drift the guard exists to
   * stop, and full-set containment is blind to it. Empty for the unboxed two:
   * `underline hover:text-foreground` is not conclusive of anything, so those
   * are matched exactly instead.
   */
  identifying: readonly string[]
}> = {
  primary: {
    purpose: 'The one committing action of a form, modal or page.',
    sizes: ['md', 'sm'], boxed: true,
    identifying: ['bg-primary', 'text-primary-foreground', 'hover:bg-primary/90'],
  },
  quiet: {
    purpose: 'Secondary action beside a primary — cancel, dismiss, a toolbar verb.',
    sizes: ['sm'], boxed: true,
    identifying: ['rounded-md', 'font-medium', 'text-muted-foreground', 'hover:bg-muted'],
  },
  outline: {
    purpose: 'Bordered toolbar / empty-state action that must read as a control.',
    sizes: ['xs'], boxed: true,
    identifying: ['rounded-md', 'border', 'border-border', 'hover:bg-muted'],
  },
  ghost: {
    purpose: 'Unlabelled icon button — collapse, expand, more.',
    sizes: ['icon', 'iconSm'], boxed: true,
    // Radius and padding drift the most, so neither is identifying: muted +
    // hover surface + lift to foreground is the ghost, at any size.
    identifying: ['text-muted-foreground', 'hover:bg-muted', 'hover:text-foreground'],
  },
  menuItem: {
    purpose: 'A row inside a popover menu.',
    sizes: ['menu'], boxed: true,
    identifying: ['rounded-sm', 'px-2', 'py-1.5', 'text-left', 'hover:bg-muted'],
  },
  link: {
    purpose: 'Inline textual action inside a sentence.',
    sizes: ['none'], boxed: false, identifying: [],
  },
  bare: {
    purpose: 'Unboxed glyph that lifts muted → foreground on hover (modal ✕, inline dismiss).',
    sizes: ['none'], boxed: false, identifying: [],
  },
}

/** Read by the guard — see EVIDENCE.identifying. */
export const BUTTON_IDENTIFYING: Record<ButtonVariant, readonly string[]> =
  Object.fromEntries((Object.keys(EVIDENCE) as ButtonVariant[]).map((v) => [v, EVIDENCE[v].identifying])) as Record<ButtonVariant, readonly string[]>

/** First evidenced size = the default. */
const DEFAULT_SIZE = Object.fromEntries(
  (Object.keys(EVIDENCE) as ButtonVariant[]).map((v) => [v, EVIDENCE[v].sizes[0]]),
) as Record<ButtonVariant, ButtonSize>

/** Every evidenced signature — read by __guards__/shared-primitives.test.ts so
 *  the guard checks the SAME strings this component renders. */
export const BUTTON_SIGNATURES: ReadonlyArray<{ variant: ButtonVariant; size: ButtonSize; boxed: boolean }> =
  (Object.keys(EVIDENCE) as ButtonVariant[]).flatMap((variant) =>
    EVIDENCE[variant].sizes.map((size) => ({ variant, size, boxed: EVIDENCE[variant].boxed })),
  )

/** The resolved class string for a (variant, size) — exported so the guard and
 *  the equivalence test read the SAME table the component renders from. */
export function buttonClasses(variant: ButtonVariant, size: ButtonSize = DEFAULT_SIZE[variant]): string {
  return VARIANT_TEMPLATE[variant](BUTTON_SIZES[size])
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required on purpose: a defaulted variant silently mis-styles a call site,
   *  and naming the intent is the whole point of the primitive. */
  variant: ButtonVariant
  size?: ButtonSize
  /** `w-full` prefix. PRIMARY_BTN was exactly this — a prop, not a variant. */
  fullWidth?: boolean
  /** Declared explicitly: a great many call sites set one and the E2E/unit
   *  tests select on it, so it must survive the refactor. */
  'data-testid'?: string
  children?: ReactNode
}

/**
 * `type` defaults to "button". A <button> inside a <form> with no type is a
 * SUBMIT button — several converted call sites relied on the old raw elements
 * carrying an explicit type, and forgetting it is a real bug, not a nit.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, fullWidth, className, type = 'button', ...rest }, ref,
) {
  return <button ref={ref} type={type} className={cx(fullWidth && 'w-full', buttonClasses(variant, size), className)} {...rest} />
})

// ── Legacy names ────────────────────────────────────────────────────────────
// One definition, three names. ModalFrame and AuthCard re-export from here so
// their old import paths keep working for anything not yet converted; the
// strings themselves now exist in exactly one place.
export const BTN_PRIMARY = buttonClasses('primary', 'md')
export const BTN_QUIET = buttonClasses('quiet', 'sm')
export const PRIMARY_BTN = `w-full ${BTN_PRIMARY}`

registerPrimitive({
  name: 'Button',
  purpose: 'Every clickable action. Variant names the INTENT; size and fullWidth are the only other axes.',
  modulePath: 'src/widgets/Button.tsx',
  states: ['default', 'disabled'],
  variants: (Object.keys(EVIDENCE) as ButtonVariant[]).map((name) => ({
    name,
    purpose: EVIDENCE[name].purpose,
    sizes: EVIDENCE[name].sizes,
  })),
  render: ({ variant, size, state }) => (
    <Button
      variant={variant as ButtonVariant}
      size={size as ButtonSize}
      disabled={state === 'disabled'}
    >
      {variant === 'ghost' || variant === 'bare' ? '✕' : 'Label'}
    </Button>
  ),
})
